import React, { useMemo, useRef, useState } from "react";
import tw from "twin.macro";
import styled, { css } from "styled-components";

const Section = styled.section`
  ${tw`w-full`}
  background: #fffbdc;
  padding: clamp(3rem, 2.2rem + 2vw, 4.5rem) 1.5rem clamp(4rem, 2.6rem + 3vw, 6rem);
`;

const Inner = tw.div`max-w-5xl mx-auto`;

const IntroBlock = tw.div`text-center max-w-3xl mx-auto`;
const Title = tw.h2`text-3xl sm:text-4xl font-semibold tracking-tight text-[#14133B]`;
const IntroText = tw.p`mt-4 text-base sm:text-lg text-[#14133B] leading-relaxed`;
const IntroSubtext = tw.p`mt-3 text-sm sm:text-base text-[#14133B] leading-relaxed`;
const NoteText = tw.p`text-xs sm:text-sm text-[#14133B] mt-2`;

const Form = styled.form`
  ${tw`mt-10 grid gap-8`}
  position: relative;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const FullWidth = styled.div`
  width: 100%;
  @media (min-width: 768px) {
    grid-column: 1 / -1;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  ${tw`text-sm font-semibold uppercase tracking-[0.18em] text-[#14133B]`}
  display: block;
`;

const baseControlStyles = ({ $hasError }) => css`
  background: #fffbdc;
  border: none;
  border-bottom: 2px solid ${$hasError ? "#E11D48" : "rgba(20, 19, 59, 0.3)"};
  padding: 10px 4px;
  font-size: 1rem;
  color: #14133b;
  transition: border-color 0.2s ease, transform 0.2s ease;
  border-radius: 0;

  &::placeholder {
    color: rgba(20, 19, 59, 0.7);
  }

  &:focus {
    outline: none;
    border-bottom-color: ${$hasError ? "#F43F5E" : "#14133b"};
    transform: translateY(-1px);
  }
`;

const Input = styled.input`
  ${baseControlStyles}
`;

const Select = styled.select`
  ${baseControlStyles}
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, #14133b 50%), linear-gradient(135deg, #14133b 50%, transparent 50%);
  background-position: calc(100% - 18px) calc(50% - 5px), calc(100% - 13px) calc(50% - 5px);
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
  padding-right: 36px;
  cursor: pointer;
`;

const Textarea = styled.textarea`
  background: #fffbdc;
  border: 2px solid rgba(20, 19, 59, 0.3);
  padding: 16px 14px;
  font-size: 1rem;
  color: #14133b;
  border-radius: 18px;
  transition: border-color 0.2s ease, transform 0.2s ease;
  min-height: 160px;
  resize: vertical;
  width: 100%;

  &::placeholder {
    color: rgba(20, 19, 59, 0.7);
  }

  &:focus {
    outline: none;
    border-color: #14133b;
    transform: translateY(-1px);
  }
`;

const ErrorText = tw.span`text-xs font-medium text-[#E11D48] mt-1`;

const SubmitRow = tw.div`mt-4 flex justify-center`;
const SubmitButton = styled.button`
  ${tw`inline-flex items-center justify-center uppercase font-semibold`}
  padding: 0.9rem 3rem;
  border-radius: 999px;
  background: #14133b !important;
  color: #fffbdc;
  letter-spacing: 0.12em;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 28px rgba(20, 19, 65, 0.18);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const RequiredNote = tw.p`text-sm text-center text-[#14133B]`;
const SubmitMessage = tw.p`mt-4 text-sm text-center`;

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  businessName: "",
  guestCount: "",
  pickupType: "",
  eventDate: "",
  eventTime: "",
  eventAddress: "",
  postalCode: "",
  preferredLocation: "",
  eventDetails: "",
};

const requiredFields = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "guestCount",
  "pickupType",
  "eventDate",
  "eventTime",
  "eventAddress",
  "postalCode",
  "preferredLocation",
];

const fieldPlaceholders = {
  firstName: "Enter first name",
  lastName: "Enter last name",
  email: "name@example.com",
  phone: "Mobile or work phone number",
  businessName: "Company / organisation (optional)",
  guestCount: "Estimated guest count",
  pickupType: "Select pick-up or delivery",
  eventDate: "Select or type your event date",
  eventTime: "Select or type your event time",
  eventAddress: "Where should we deliver or serve?",
  postalCode: "Enter postcode",
  preferredLocation: "Select a Grain location",
};


const pickupOptions = [
  { value: "", label: "Select pick-up or delivery" },
  { value: "pickup", label: "Pickup" },
  { value: "delivery", label: "Delivery" },
];

const locationOptions = [
  { value: "", label: "Select a Grain location" },
  { value: "holborn", label: "Holborn" },
  { value: "other", label: "Other / Not listed" },
];

const CateringQuoteForm = React.forwardRef((_, ref) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const fieldRefs = useRef({});

  const fieldDefinitions = useMemo(
    () => [
      { name: "firstName", label: "First Name*", type: "text" },
      { name: "lastName", label: "Last Name*", type: "text" },
      { name: "email", label: "Email Address*", type: "email" },
      { name: "phone", label: "Phone Number*", type: "tel" },
      { name: "businessName", label: "Business Name", type: "text" },
      { name: "guestCount", label: "Number of Guests*", type: "number", min: "0" },
      { name: "pickupType", label: "Pickup Type*", type: "select", options: pickupOptions },
      { name: "eventDate", label: "Event Date*", type: "date" },
      { name: "eventTime", label: "Event Time*", type: "time" },
      { name: "eventAddress", label: "Event Address*", type: "text" },
      { name: "postalCode", label: "PostCode*", type: "text" },
      { name: "preferredLocation", label: "Preferred Location*", type: "select", options: locationOptions },
    ],
    []
  );

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!values[field]?.toString().trim()) {
        newErrors[field] = "This field is required.";
      }
    });

    if (values.phone && values.phone.replace(/\D/g, "").length < 6) {
      newErrors.phone = "Please enter a valid phone number.";
    }

    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    return newErrors;
  };

  const scrollToField = (fieldName) => {
    const node = fieldRefs.current[fieldName];
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      node.querySelector("input, select, textarea")?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage(null);
    setSubmitError(null);

    const validationErrors = validate();

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      scrollToField(Object.keys(validationErrors)[0]);
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch("/api/SendCateringOrder/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message = data?.message || "Unable to submit request.";
        const serverErrors = data?.errors || {};
        setSubmitError(message);
        setErrors((prev) => ({ ...prev, ...serverErrors }));
        const firstServerError = Object.keys(serverErrors)[0];
        if (firstServerError) {
          scrollToField(firstServerError);
        }
        return;
      }

      setSubmitMessage("Thanks! Your request has been sent. We'll be in touch shortly.");
      setValues(initialValues);
      setErrors({});
    } catch (error) {
      setSubmitError("Submission failed. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section ref={ref} id="catering-quote">
      <Inner>
        <IntroBlock>
          <Title>Get a Custom Catering Quote</Title>
          <IntroSubtext>
            Want to know more or need help with an existing order? Contact support for hands-on assistance.
          </IntroSubtext>
        </IntroBlock>

        <Form
          ref={(node) => {
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          onSubmit={handleSubmit}
          noValidate
        >
          <RequiredNote>*Required field</RequiredNote>

          <Grid>
            {fieldDefinitions.map(({ name, label, type, options, ...rest }) => {
              const commonProps = {
                id: name,
                name,
                value: values[name],
                placeholder: fieldPlaceholders[name] ?? "",
                onChange: (event) => handleChange(name, event.target.value),
                $hasError: Boolean(errors[name]),
                ...rest,
              };

              const Control =
                type === "select" ? (
                  <Select {...commonProps}>
                    {options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Input type={type} {...commonProps} />
                );

              return (
                <Field key={name} ref={(node) => (fieldRefs.current[name] = node)}>
                  <Label htmlFor={name}>{label}</Label>
                  {Control}
                  {errors[name] && <ErrorText>{errors[name]}</ErrorText>}
                </Field>
              );
            })}
          </Grid>

          <FullWidth ref={(node) => (fieldRefs.current.eventDetails = node)}>
            <Label htmlFor="eventDetails">Tell Us About Your Event</Label>
            <Textarea
              id="eventDetails"
              name="eventDetails"
              value={values.eventDetails}
              placeholder="Share menu preferences, budget, venue details, or special requests."
              onChange={(event) => handleChange("eventDetails", event.target.value)}
              $hasError={Boolean(errors.eventDetails)}
            />
            <NoteText>
              For privacy and security purposes, please do not share payment information in this field.
            </NoteText>
          </FullWidth>
          <SubmitRow>
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting…" : "Submit Request"}
            </SubmitButton>
          </SubmitRow>

          {submitMessage && <SubmitMessage css={[tw`text-[#2F855A]`]}>{submitMessage}</SubmitMessage>}
          {submitError && <SubmitMessage css={[tw`text-[#E53E3E]`]}>{submitError}</SubmitMessage>}
        </Form>
      </Inner>
    </Section>
  );
});

export default CateringQuoteForm;
